/**
 * useWebSocketEvent Hook
 *
 * Simplifies WebSocket event subscription by wrapping the common pattern
 * of useEffect + on() + cleanup.
 */

import { useEffect, useRef } from "react";
import { useWebSocket } from "./WebSocketContext";
import { WebSocketEventMap } from "./connectionManager";

// Overloaded function signatures for type-safe event handlers
export function useWebSocketEvent<K extends keyof WebSocketEventMap>(
  eventType: K,
  handler: (data: WebSocketEventMap[K]) => void,
  options?: {
    enabled?: boolean;
  }
): void;
export function useWebSocketEvent<T = unknown>(
  eventType: string,
  handler: (data: T) => void,
  options?: {
    enabled?: boolean;
  }
): void;
export function useWebSocketEvent<T = unknown>(
  eventType: string,
  handler: (data: T) => void,
  options?: {
    enabled?: boolean;
  }
) {
  const { on, connectionState } = useWebSocket();
  const enabled = options?.enabled !== false;

  // Store the latest handler in a ref to avoid re-subscribing on every render
  const handlerRef = useRef(handler);

  // Update ref when handler changes
  useEffect(() => {
    handlerRef.current = handler;
  }, [handler]);

  useEffect(() => {
    if (!enabled) return;

    // Warn if WebSocket is not configured (connectionState will be "disconnected" and never change)
    if (connectionState === "disconnected") {
      const WS_URL = process.env.REACT_APP_WEBSOCKET_URL || "";
      if (!WS_URL) {
        console.warn(
          `WebSocket not configured (REACT_APP_WEBSOCKET_URL missing) - event "${eventType}" will not be received`
        );
        return; // Don't subscribe if WebSocket is not configured
      }
    }

    // Use a wrapper that calls the latest handler from the ref
    const wrappedHandler = (data: any) => {
      handlerRef.current(data);
    };

    const unsubscribe = on(eventType, wrappedHandler);
    return unsubscribe;
  }, [eventType, on, connectionState, enabled]);
}
